/**
 * Filename validation utility for file uploads
 * Provides client-side validation to match backend sanitization logic
 */

export interface FilenameValidationResult {
  isValid: boolean;
  hasProblematicCharacters: boolean;
  sanitizedFilename: string | null;
  warnings: string[];
}

/**
 * Validates filename and suggests sanitized version
 * Based on the backend sanitization logic in ProfileConcerns::FilenameSanitization
 */
export function validateFilename(originalFilename: string): FilenameValidationResult {
  if (!originalFilename || originalFilename.trim().length === 0) {
    return {
      isValid: false,
      hasProblematicCharacters: false,
      sanitizedFilename: null,
      warnings: ['Filename cannot be empty']
    };
  }

  const warnings: string[] = [];
  let hasProblematicCharacters = false;

  // Get file extension
  const fileExtension = getFileExtension(originalFilename);
  const basename = getBasename(originalFilename, fileExtension);

  // Check for emojis and special Unicode characters
  const hasEmojis = containsEmojis(basename);
  const hasSpecialUnicode = containsSpecialUnicodeCharacters(basename);
  const hasProblematicFileChars = containsProblematicFilenameCharacters(basename);

  if (hasEmojis) {
    hasProblematicCharacters = true;
    warnings.push('Filename contains emojis that will be removed');
  }

  if (hasSpecialUnicode) {
    hasProblematicCharacters = true;
    warnings.push('Filename contains special characters that will be removed');
  }

  if (hasProblematicFileChars) {
    hasProblematicCharacters = true;
    warnings.push('Filename contains characters that are not allowed in file names');
  }

  // Generate sanitized filename
  let sanitizedFilename: string | null = null;
  if (hasProblematicCharacters) {
    sanitizedFilename = sanitizeFilename(originalFilename);
    
    // If sanitized filename is too different or empty, suggest a generic name
    if (!sanitizedFilename || sanitizedFilename.length < 3 || sanitizedFilename === `resume${fileExtension}`) {
      sanitizedFilename = `resume${fileExtension}`;
      warnings.push('Most characters were removed, suggesting generic filename');
    }
  }

  return {
    isValid: true, // We don't block uploads, just warn
    hasProblematicCharacters,
    sanitizedFilename,
    warnings
  };
}

/**
 * Client-side sanitization that mirrors the backend logic
 */
function sanitizeFilename(filename: string): string {
  const fileExtension = getFileExtension(filename);
  let basename = getBasename(filename, fileExtension);

  // Remove emoji and Unicode characters
  basename = removeEmojiAndUnicodeCharacters(basename);
  
  // Remove problematic filename characters
  basename = removeProblematicFilenameCharacters(basename);

  // Ensure we have a valid filename
  if (!basename || basename.trim().length === 0) {
    basename = 'resume';
  }

  // Limit length
  basename = truncateFilename(basename, 100);

  return `${basename}${fileExtension.toLowerCase()}`;
}

/**
 * Remove emojis and special Unicode characters
 */
function removeEmojiAndUnicodeCharacters(basename: string): string {
  return basename
    // Remove emojis and special Unicode ranges
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Misc Symbols and Pictographs
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport and Map Symbols
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // Regional Indicator Symbols
    .replace(/[\u{2600}-\u{26FF}]/gu, '')   // Miscellaneous Symbols
    .replace(/[\u{2700}-\u{27BF}]/gu, '')   // Dingbats
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')   // Variation Selectors
    .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Supplemental Symbols and Pictographs
    .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Symbols and Pictographs Extended-A
    .replace(/[\u{E000}-\u{F8FF}]/gu, '')   // Private Use Area
    // Keep only basic Latin ranges
    .replace(/[^\u{0000}-\u{007F}\u{00A0}-\u{00FF}\u{0100}-\u{017F}\u{0180}-\u{024F}]/gu, '')
    .trim();
}

/**
 * Remove problematic filename characters
 */
function removeProblematicFilenameCharacters(basename: string): string {
  return basename
    .replace(/[<>:"|?*]/g, '')        // Windows forbidden characters
    .replace(/[/\\]/g, '_')           // Path separators
    .replace(/\s+/g, '_')             // Replace spaces with underscores
    .replace(/[^\w\-_.()]/g, '')      // Keep only word characters, hyphens, underscores, dots, and parentheses
    .replace(/_{2,}/g, '_')           // Replace multiple underscores with single
    .replace(/^[._-]+/, '');          // Remove leading dots, underscores, hyphens (but not trailing)
}

/**
 * Check if filename contains emojis
 */
function containsEmojis(filename: string): boolean {
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}]/u;
  return emojiRegex.test(filename);
}

/**
 * Check if filename contains special Unicode characters
 */
function containsSpecialUnicodeCharacters(filename: string): boolean {
  // Test for non-basic Latin characters
  const basicLatinRegex = /[^\u{0000}-\u{007F}\u{00A0}-\u{00FF}\u{0100}-\u{017F}\u{0180}-\u{024F}]/u;
  return basicLatinRegex.test(filename);
}

/**
 * Check if filename contains problematic characters for filenames
 */
function containsProblematicFilenameCharacters(filename: string): boolean {
  // Check for Windows forbidden characters and other problematic ones
  const problematicRegex = /[<>:"|?*\/\\]/;
  return problematicRegex.test(filename);
}

/**
 * Get file extension including the dot
 */
function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
}

/**
 * Get basename without extension
 */
function getBasename(filename: string, extension: string): string {
  return extension ? filename.slice(0, -extension.length) : filename;
}

/**
 * Truncate filename to max length
 */
function truncateFilename(filename: string, maxLength: number): string {
  return filename.length <= maxLength ? filename : filename.substring(0, maxLength);
}

/**
 * Generate user-friendly message for filename validation
 */
export function getFilenameValidationMessage(result: FilenameValidationResult): string {
  if (!result.hasProblematicCharacters) {
    return '';
  }

  let message = 'Your filename will be automatically cleaned up: ';
  
  if (result.sanitizedFilename) {
    message += `"${result.sanitizedFilename}"`;
  }

  if (result.warnings.length > 0) {
    message += '\n\nReasons:\n• ' + result.warnings.join('\n• ');
  }

  return message;
}